/**
 * RisTariffaHealth — Mini-dashboard admin "Health RiS Tariffa".
 *
 * Mostra in semaforino lo stato production_ready dei 8 moduli RiS:
 *   - 3 Extra Ricovero (alta_diagnostica, diagnostica_completa, visite_specialistiche)
 *   - franchigie_rsm
 *   - target aggregato + 3 sezioni (kids/dental/fisio)
 *
 * Endpoint: GET /api/admin/ris/extraction/status (Fase 11D TC6).
 * Toggle: POST /api/admin/ris/extraction/production-ready.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { CheckCircle2, AlertTriangle, RefreshCw, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const MODULI = [
  { key: "alta_diagnostica", label: "Extra Ricovero — Alta Diagnostica", toggle: true },
  { key: "diagnostica_completa", label: "Extra Ricovero — Diagnostica Completa", toggle: true },
  { key: "visite_specialistiche", label: "Extra Ricovero — Visite Specialistiche", toggle: true },
  { key: "franchigie_rsm", label: "Franchigie RSM (B-H × Comfort/Deluxe)", toggle: false },
  { key: "target", label: "Formula Target — Aggregato (Mod. 5168)", toggle: false },
  { key: "target_kids", label: "  └ Sezione Kids", toggle: false },
  { key: "target_dental", label: "  └ Sezione Dental", toggle: false },
  { key: "target_fisio", label: "  └ Sezione Fisio", toggle: false },
];

export default function RisTariffaHealth() {
  const [status, setStatus] = useState(null);
  const [prevStatus, setPrevStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toggling, setToggling] = useState(null);  // key del modulo in toggle
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [regression, setRegression] = useState([]);  // moduli regrediti

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/api/admin/ris/extraction/status`, { withCredentials: true });
      setStatus((prev) => {
        // rileva regressioni PRODUCTION → SAFE-MODE
        if (prev) {
          const regr = [];
          for (const k of Object.keys(r.data)) {
            if (prev[k]?.production_ready && !r.data[k]?.production_ready) {
              regr.push(k);
            }
          }
          setRegression(regr);
          setPrevStatus(prev);
        }
        return r.data;
      });
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh ogni 60s (cleanup su unmount o toggle off)
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => { load(); }, 60000);
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  const handleToggle = async (key) => {
    if (!window.confirm(`Toggle production_ready per "${key}"?`)) return;
    setToggling(key);
    try {
      await axios.post(`${API}/api/admin/ris/extraction/production-ready`,
        { opzione: key, value: !(status?.[key]?.production_ready) },
        { withCredentials: true });
      await load();
    } catch (e) {
      alert("Toggle fallito: " + (e.response?.data?.detail?.message || e.message));
    } finally {
      setToggling(null);
    }
  };

  if (loading && !status) {
    return (
      <div className="flex items-center gap-2 text-sm text-zinc-500" data-testid="ris-health-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Caricamento stato moduli RiS...
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-4xl" data-testid="ris-tariffa-health">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900">Health RiS Tariffa</h2>
          <p className="text-sm text-zinc-500">
            Stato live degli 8 moduli tariffari Reale Mutua RiS (Mod. 3013/12 INF Ed. 07/2023).
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-xs text-zinc-600 cursor-pointer select-none" data-testid="ris-health-auto-refresh-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="accent-zinc-900"
              data-testid="ris-health-auto-refresh-input"
            />
            Auto-refresh 60s
          </label>
          <button onClick={load} disabled={loading} data-testid="ris-health-refresh"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50 transition-colors">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Aggiorna
          </button>
        </div>
      </div>

      {regression.length > 0 && (
        <div className="p-3 rounded-lg bg-rose-50 border-2 border-rose-300 text-sm text-rose-800" data-testid="ris-health-regression-banner">
          <strong className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Regressione rilevata
          </strong>
          <p className="mt-1 text-xs">
            I seguenti moduli sono passati da <strong>PRODUCTION</strong> a <strong>SAFE-MODE</strong>:
          </p>
          <ul className="mt-1 text-xs list-disc list-inside font-mono">
            {regression.map((k) => <li key={k} data-testid={`ris-health-regressed-${k}`}>{k}</li>)}
          </ul>
        </div>
      )}

      {error && (
        <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700" data-testid="ris-health-error">
          <strong>Errore:</strong> {error}
        </div>
      )}

      <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Modulo</th>
              <th className="text-center px-4 py-3 font-medium">Status</th>
              <th className="text-center px-4 py-3 font-medium">Completezza</th>
              <th className="text-center px-4 py-3 font-medium">Toggle</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {MODULI.map((m) => {
              const s = status?.[m.key];
              if (!s) {
                return (
                  <tr key={m.key} data-testid={`ris-health-row-${m.key}-missing`}>
                    <td className="px-4 py-3 text-zinc-400">{m.label}</td>
                    <td colSpan={3} className="px-4 py-3 text-center text-zinc-400">— non disponibile —</td>
                  </tr>
                );
              }
              const ready = s.production_ready;
              const completo = s.total_expected_cells > 0 && s.completed_cells >= s.total_expected_cells;
              return (
                <tr key={m.key} className="hover:bg-zinc-50/50 transition-colors" data-testid={`ris-health-row-${m.key}`}>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-700 whitespace-pre">{m.label}</td>
                  <td className="px-4 py-3 text-center">
                    {ready ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium" data-testid={`ris-health-badge-${m.key}-prod`}>
                        <CheckCircle2 className="w-3 h-3" /> PRODUCTION
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-medium" data-testid={`ris-health-badge-${m.key}-safe`}>
                        <AlertTriangle className="w-3 h-3" /> SAFE-MODE
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center text-xs">
                    <span className={completo ? "text-emerald-700 font-medium" : "text-amber-700 font-medium"}>
                      {s.completed_cells}/{s.total_expected_cells}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {m.toggle ? (
                      <button onClick={() => handleToggle(m.key)} disabled={toggling === m.key || !completo}
                        data-testid={`ris-health-toggle-${m.key}`}
                        title={!completo ? "Modulo non completo: toggle disabilitato" : ""}
                        className="px-2.5 py-1 text-xs font-medium rounded-md bg-zinc-100 text-zinc-700 hover:bg-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                        {toggling === m.key ? <Loader2 className="w-3 h-3 animate-spin inline" /> : (ready ? "Disattiva" : "Attiva")}
                      </button>
                    ) : (
                      <span className="text-zinc-400 text-xs">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-zinc-500 flex items-start gap-2">
        <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0 text-amber-500" />
        <p>
          Solo i moduli <strong>Extra Ricovero</strong> hanno toggle individuale.
          Franchigie RSM e Target richiedono toggle via script di sistema (mantengono `production_ready` aggregato).
        </p>
      </div>
    </div>
  );
}
